import React, { useEffect } from 'react';
import { GymInfraProvider, useGymInfra } from './GymInfraContext';
import { UIProvider, useUI } from './UIContext';
import { MemberProvider, useMembers } from './MemberContext';
import { MembershipProvider, useMemberships } from './MembershipContext';
import { FacilityProvider, useFacility } from './FacilityContext';

// dataLoading 동기화 컴포넌트: 모든 도메인 Context 로드 완료 시 dataLoading = false
function LoadingSync({ children }) {
  const { setDataLoading } = useUI();
  const { _memberLoaded } = useMembers();
  const { _membershipLoaded } = useMemberships();
  const { _facilityLoaded } = useFacility();

  useEffect(() => {
    if (_memberLoaded && _membershipLoaded && _facilityLoaded) {
      setDataLoading(false);
    }
  }, [_memberLoaded, _membershipLoaded, _facilityLoaded, setDataLoading]);

  return children;
}

export const AppProvider = ({ children, adminGymId = null }) => (
  <GymInfraProvider adminGymId={adminGymId}>
    <UIProvider>
      <MemberProvider>
        <MembershipProvider>
          <FacilityProvider>
            <LoadingSync>
              {children}
            </LoadingSync>
          </FacilityProvider>
        </MembershipProvider>
      </MemberProvider>
    </UIProvider>
  </GymInfraProvider>
);

// 호환 레이어: 기존 useApp() 호출을 그대로 지원
export const useApp = () => {
  const infra = useGymInfra();
  const ui = useUI();
  const member = useMembers();
  const membership = useMemberships();
  const facility = useFacility();

  return {
    // GymInfra
    gymId: infra.gymId,
    isAdminMode: infra.isAdminMode,
    // UI
    darkMode: ui.darkMode,
    setDarkMode: ui.setDarkMode,
    toasts: ui.toasts,
    showToast: ui.showToast,
    removeToast: ui.removeToast,
    dataLoading: ui.dataLoading,
    // Member
    members: member.members,
    addMember: member.addMember,
    updateMember: member.updateMember,
    deleteMember: member.deleteMember,
    trainers: member.trainers,
    addTrainer: member.addTrainer,
    updateTrainer: member.updateTrainer,
    deleteTrainer: member.deleteTrainer,
    attendances: member.attendances,
    addAttendance: member.addAttendance,
    deleteAttendance: member.deleteAttendance,
    fetchAttendancesByMonth: member.fetchAttendancesByMonth,
    approveMember: member.approveMember,
    rejectMember: member.rejectMember,
    // Membership
    membershipProducts: membership.membershipProducts,
    addMembershipProduct: membership.addMembershipProduct,
    updateMembershipProduct: membership.updateMembershipProduct,
    deleteMembershipProduct: membership.deleteMembershipProduct,
    memberships: membership.memberships,
    addMembership: membership.addMembership,
    updateMembership: membership.updateMembership,
    deleteMembership: membership.deleteMembership,
    freezeMembership: membership.freezeMembership,
    unfreezeMembership: membership.unfreezeMembership,
    ptProducts: membership.ptProducts,
    addPtProduct: membership.addPtProduct,
    updatePtProduct: membership.updatePtProduct,
    deletePtProduct: membership.deletePtProduct,
    ptAssignments: membership.ptAssignments,
    addPtAssignment: membership.addPtAssignment,
    updatePtAssignment: membership.updatePtAssignment,
    refreshPtData: membership.refreshPtData,
    ptLogs: membership.ptLogs,
    addPtLog: membership.addPtLog,
    updatePtLog: membership.updatePtLog,
    deletePtLog: membership.deletePtLog,
    renewalRequests: membership.renewalRequests,
    approveRenewal: membership.approveRenewal,
    rejectRenewal: membership.rejectRenewal,
    // Facility
    lockers: facility.lockers,
    addLockers: facility.addLockers,
    addLocker: facility.addLocker,
    updateLocker: facility.updateLocker,
    deleteLocker: facility.deleteLocker,
    gymSettings: facility.gymSettings,
    updateGymSettings: facility.updateGymSettings,
    profileEditRequests: facility.profileEditRequests,
    approveProfileEdit: facility.approveProfileEdit,
    rejectProfileEdit: facility.rejectProfileEdit,
    exportBackup: facility.exportBackup,
    importBackup: facility.importBackup,
    exportMembersCSV: facility.exportMembersCSV,
    resetAllData: facility.resetAllData,
  };
};
